import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Timer, Globe, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function formatCountdown(seconds) {
  if (seconds <= 0) return "NOW";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function EconomicCalendarWidget({ lang }) {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  const fetchCalendar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/economic-calendar`);
      setData(res.data);
    } catch (e) {
      console.error("Calendar fetch error:", e);
    }
  }, []);

  useEffect(() => {
    fetchCalendar();
    const interval = setInterval(fetchCalendar, 60000);
    return () => clearInterval(interval);
  }, [fetchCalendar]);

  // Tick every second for countdown
  useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;

  const sessions = data.market_sessions || [];
  const events = data.economic_events || [];

  // Decrease countdowns by tick
  const adjustedSessions = sessions.map(s => ({
    ...s,
    countdown_seconds: Math.max(0, s.countdown_seconds - tick),
  }));
  const adjustedEvents = events.map(e => ({
    ...e,
    countdown_seconds: Math.max(0, e.countdown_seconds - tick),
  }));

  return (
    <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="economic-calendar">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
          <Timer className="w-4 h-4 text-blue-400" />
          {lang === "ar" ? "جلسات السوق والاحداث الاقتصادية" : "Market Sessions & Economic Events"}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {/* Market Sessions */}
        <div className="flex flex-wrap gap-2 mb-3">
          {adjustedSessions.map((s) => (
            <div
              key={s.name}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${
                s.is_open
                  ? "border-green-700 bg-green-950/50"
                  : "border-zinc-700 bg-zinc-800/50"
              }`}
              data-testid={`session-${s.name.toLowerCase().replace(' ', '-')}`}
            >
              <span className={`w-2 h-2 rounded-full ${s.is_open ? "bg-green-400 animate-pulse" : "bg-zinc-500"}`} />
              <span className="text-zinc-300 font-medium">{lang === "ar" ? s.name_ar : s.name}</span>
              <span className={`font-mono ${s.is_open ? "text-green-400" : "text-zinc-500"}`}>
                {s.is_open
                  ? `${lang === "ar" ? "يغلق" : "closes"} ${formatCountdown(s.countdown_seconds)}`
                  : `${lang === "ar" ? "يفتح" : "opens"} ${formatCountdown(s.countdown_seconds)}`
                }
              </span>
            </div>
          ))}
        </div>

        {/* Economic Events */}
        {adjustedEvents.length > 0 && (
          <div className="space-y-1">
            {adjustedEvents.slice(0, 6).map((e, i) => {
              const isImminent = e.countdown_seconds < 3600 && !e.is_past;
              const isHigh = e.impact === "High";
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                    isImminent ? "bg-yellow-950/40 border border-yellow-700/50" :
                    e.is_past ? "opacity-50" : ""
                  }`}
                  data-testid={`event-${i}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isHigh ? (
                      <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" />
                    ) : (
                      <Globe className="w-3 h-3 text-zinc-500 shrink-0" />
                    )}
                    <Badge className={`text-[10px] px-1.5 py-0 ${
                      isHigh ? "bg-red-700" : "bg-zinc-700"
                    }`}>
                      {e.country}
                    </Badge>
                    <span className={`truncate ${isHigh ? "text-zinc-200 font-medium" : "text-zinc-400"}`}>
                      {e.title}
                    </span>
                  </div>
                  <span className={`font-mono shrink-0 ml-2 ${
                    isImminent ? "text-yellow-400 font-bold" :
                    e.is_past ? "text-zinc-600" :
                    e.countdown_seconds < 7200 ? "text-orange-400" : "text-zinc-400"
                  }`}>
                    {e.is_past ? (lang === "ar" ? "انتهى" : "DONE") : formatCountdown(e.countdown_seconds)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
